import React from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

/**
 * LogoMarquee — auto-scrolling continuous strip of client/partner logos.
 * Uses react-fast-marquee (already in deps). Pause on hover, colour-on-hover.
 */
export const LogoMarquee = ({
  items = [],
  eyebrow = "Our Clients",
  titleTop = "Trusted by brands",
  titleAccent = "we're proud to serve.",
  subtitle = "",
  footnote = "",
  speed = 38,
  testId = "logo-marquee-section",
}) => {
  if (!items || items.length === 0) return null;

  // duplicate items for visual density
  const loop = [...items, ...items];

  return (
    <section data-testid={testId} className="relative py-24 md:py-32 bg-slate-50 border-t border-slate-100 overflow-hidden">
      <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14 max-w-3xl"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600 font-bold flex items-center gap-2">
            <span className="w-8 h-px bg-amber-500" />
            {eyebrow}
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-bold text-navy-900 tracking-[-0.03em] leading-[1]">
            {titleTop}
            <br />
            <span className="text-amber-500">{titleAccent}</span>
          </h2>
          {subtitle && (
            <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
              {subtitle}
            </p>
          )}
        </motion.div>
      </div>

      <div className="relative marquee-mask">
        <Marquee
          autoFill
          pauseOnHover
          speed={speed}
          gradient={false}
          className="py-2"
        >
          {loop.map((c, i) => (
            <div
              key={`${c.name}-${i}`}
              data-testid={`marquee-logo-${c.name.toLowerCase()}`}
              className="group mx-5 md:mx-8 flex items-center justify-center"
            >
              <div className="h-28 md:h-32 w-44 md:w-56 rounded-2xl bg-white border border-slate-200 px-7 md:px-9 py-5 flex items-center justify-center hover:border-amber-500 hover:shadow-lg hover:-translate-y-1 transition-all duration-500">
                <img
                  src={c.logo}
                  alt={c.name}
                  loading="lazy"
                  className="max-w-full max-h-full object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                />
              </div>
            </div>
          ))}
        </Marquee>
      </div>

      {footnote && (
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 mt-12 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-mono">
          <ShieldCheck className="w-4 h-4 text-amber-500" />
          {footnote}
        </div>
      )}
    </section>
  );
};

export default LogoMarquee;
